/* eslint-disable import/no-unresolved, import/no-extraneous-dependencies, no-return-await, no-nested-ternary */
import React, { useEffect, useCallback } from 'react';
import { Navigate, NavLink, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import useNucleusSession from '@omniverse/auth/react/hooks/NucleusSession';
import { useCallbackRef } from '@mantine/hooks';
import { useAlert, AlertType } from 'src/components/AlertsProvider';
import ModalDialog from 'src/components/ModalDialog';
import {
  OmniverseHeader,
  LoaderError,
  Placeholder,
  SessionDuration,
  SessionStatus,
  SessionTerminateButton,
  StreamLoader,
  StreamError,
} from '../components';
import { useOmniverseConfig } from '../hooks/useConfig';
import useStreamStart, {
  streamStartNotification,
} from '../hooks/useStreamStart';
import { getSessions, StreamingSessionPage } from '../state/Sessions';
import { AuthenticationType, getStreamingApp } from '../state/Apps';

export default function OmniverseAppStreamList() {
  const config = useOmniverseConfig();
  const nucleus = useNucleusSession();

  const { appId = '' } = useParams<{ appId: string }>();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['streaming-app', appId],
    queryFn: async () =>
      await getStreamingApp({
        appId,
        config,
      }),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const query = useQuery({
    queryKey: ['app-sessions', appId],
    queryFn: async () => {
      return await getSessions({ config, appId, status: 'alive' });
    },
    enabled: !!appId,
  });

  function cancelStream() {
    window.close();
  }

  if (!appId) {
    cancelStream();
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <OmniverseHeader />
        <Box sx={{ position: 'relative' }}>
          <StreamLoader />
        </Box>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <OmniverseHeader />
        <LoaderError title={'Failed to load the stream'}>
          {error.toString()}
        </LoaderError>
      </Box>
    );
  }

  if (data?.authType === AuthenticationType.nucleus && !nucleus.established) {
    return (
      <Navigate
        to={`/omniverse/nucleus/authenticate?redirectAfter=/omniverse/app/${appId}/sessions`}
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <OmniverseHeader />
      <Box sx={{ position: 'relative' }}>
        {query.isLoading ? (
          <StreamLoader />
        ) : query.isError ? (
          <LoaderError>{query.error.toString()}</LoaderError>
        ) : (
          <AppStreamListModal
            appId={appId}
            query={query}
            onSessionCancel={cancelStream}
          />
        )}
      </Box>
    </Box>
  );
}

function AppStreamListModal({
  appId,
  query,
  onSessionCancel,
}: {
  appId: string;
  query: UseQueryResult<StreamingSessionPage>;
  onSessionCancel: () => void;
}) {
  const streamStart = useStreamStart(appId);
  const { showAlert } = useAlert();

  const startNewSession = useCallbackRef(() => {
    showAlert({
      id: streamStartNotification,
      type: AlertType.Info,
      text: 'Starting a new streaming session...',
    });

    streamStart.mutate();
  });

  const reload = () => {
    window.location.reload();
  };

  const autoStart =
    query.isSuccess && !query.data.items.length && !streamStart.isError;

  useEffect(() => {
    if (autoStart) {
      startNewSession();
    }
  }, [autoStart, startNewSession]);

  if (autoStart || streamStart.isPending) {
    return <StreamLoader />;
  }

  if (streamStart.isError) {
    return (
      <StreamError
        disabled={streamStart.isPending}
        loading={streamStart.isPending}
        error={streamStart.error}
        onReload={reload}
        onStartNewSession={startNewSession}
      />
    );
  }

  return (
    <ModalDialog
      open
      title="Sessions"
      onClose={onSessionCancel}
      maxWidth="lg"
      fullWidth
      showCancelBtn={false}
      showConfirmBtn={false}
    >
      <Box sx={{ minHeight: 400 }}>
        {query.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : query.isError ? (
          <LoaderError>{query.error.toString()}</LoaderError>
        ) : query.data && query.data.items.length > 0 ? (
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: '3px 3px 5px #E8E8E8',
              border: '0.1rem solid #E8E8E8',
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: '1.575rem' }}>
                    #
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '1.575rem' }}>
                    User
                  </TableCell>
                  <TableCell
                    sx={{ width: 125, fontWeight: 600, fontSize: '1.575rem' }}
                  >
                    Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '1.575rem' }}>
                    Start date
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '1.575rem' }}>
                    End date
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '1.575rem' }}>
                    Duration
                  </TableCell>
                  <TableCell
                    sx={{ width: 280, fontWeight: 600, fontSize: '1.575rem' }}
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {query.data?.items.map((session) => (
                  <TableRow
                    key={session.id}
                    sx={{
                      height: 50,
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                    }}
                  >
                    <TableCell sx={{ fontSize: '1.53rem' }}>
                      {session.id}
                    </TableCell>
                    <TableCell sx={{ fontSize: '1.53rem' }}>
                      {session.userName}
                    </TableCell>
                    <TableCell>
                      <SessionStatus status={session.status} />
                    </TableCell>
                    <TableCell sx={{ fontSize: '1.53rem' }}>
                      {session.startDate.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ fontSize: '1.53rem' }}>
                      {session.endDate ? session.endDate.toLocaleString() : ''}
                    </TableCell>
                    <TableCell sx={{ fontSize: '1.53rem' }}>
                      <SessionDuration session={session} />
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          gap: 1,
                        }}
                      >
                        {session.status === 'IDLE' && (
                          <Button
                            component={NavLink}
                            to={`/omniverse/app/${appId}/sessions/${session.id}`}
                            variant="contained"
                            size="small"
                          >
                            Reconnect
                          </Button>
                        )}
                        {session.status !== 'STOPPED' && (
                          <SessionTerminateButton session={session} />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Placeholder
            icon={
              <DesktopWindowsOutlinedIcon
                sx={{ fontSize: 100, color: 'currentColor' }}
              />
            }
          >
            You don&apos;t have any sessions at the moment.
          </Placeholder>
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={startNewSession}
          autoFocus
        >
          New session
        </Button>
      </Box>
    </ModalDialog>
  );
}
